import { useState, useEffect, useCallback } from "react";
import { useCountryApi } from "../../hooks/useCountryApi";

export const useAllCountriesViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const {
    getCountries,
    deleteCountry,
    createCountry,
    updateCountry: apiUpdateCountry,
    cacheOperations: { readCountries, writeCountries },
  } = useCountryApi();

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const countriesResult = await getCountries();
      if (!countriesResult.success) {
        throw new Error("Failed to fetch countries");
      }
      writeCountries(countriesResult.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [getCountries, writeCountries]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateCountry = useCallback(
    async (formData) => {
      try {
        const result = await createCountry(formData);
        if (result.success) {
          setSuccessModal("Country created successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error creating country:", error);
        throw new Error(error.message);
      }
    },
    [createCountry]
  );

  const handleUpdateCountry = useCallback(
    async (countryId, formData) => {
      try {
        const result = await apiUpdateCountry(countryId, formData);
        if (result.success) {
          setSuccessModal("Country updated successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error updating country:", error);
        throw new Error(error.message);
      }
    },
    [apiUpdateCountry]
  );

  const handleDelete = useCallback(
    async (country) => {
      try {
        const result = await deleteCountry(country.country_id);
        if (result.success) {
          setSuccessModal("Country deleted successfully");
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error deleting country:", err);
      }
    },
    [deleteCountry]
  );

  return {
    countries: readCountries(),
    createCountry: handleCreateCountry,
    updateCountry: handleUpdateCountry,
    handleDelete,
    successModal,
    setSuccessModal,
  };
};
